
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth";
import { useToast } from "@/components/ui/use-toast";
import { PasswordResetForm } from "@/components/password-reset/PasswordResetForm";
import { InvalidTokenCard } from "@/components/password-reset/InvalidTokenCard";
import { ResetSuccessDialog } from "@/components/password-reset/ResetSuccessDialog";

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Cette fonction va tenter d'extraire le token de toutes les sources possibles
  const extractToken = () => {
    console.log("Extracting password reset token - Full debug");
    console.log("Current full URL:", window.location.href);
    console.log("Location details:", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      href: window.location.href,
      origin: window.location.origin
    });
    
    // Liste complète des endroits où le token pourrait se trouver
    const possibleSources = [
      { name: "searchParams", value: searchParams.get("token") },
      { name: "location.search", value: new URLSearchParams(location.search).get("token") },
      { name: "hash fragment query", value: location.hash.includes("?") ? 
          new URLSearchParams(location.hash.split("?")[1]).get("token") : null },
      { name: "window.location.search", value: new URLSearchParams(window.location.search).get("token") },
      { name: "full hash fragment", value: location.hash.replace(/^#\/reset-password\??/, "") },
      { name: "complex hash extraction", value: (() => {
        const hashParts = window.location.hash.split('?');
        return hashParts.length > 1 ? new URLSearchParams(hashParts[1]).get("token") : null;
      })() }
    ];
    
    console.log("Checking all possible token sources:", possibleSources);
    
    // Essayer chaque source
    for (const source of possibleSources) {
      if (source.value) {
        console.log(`Token found in ${source.name}:`, 
          source.value.substring(0, 6) + "...");
        return source.value;
      }
    }
    
    // Essayer d'utiliser un regex comme dernier recours
    const tokenRegex = /[?&#]token=([^&#]*)/;
    const match = window.location.href.match(tokenRegex);
    if (match && match[1]) {
      const extractedToken = decodeURIComponent(match[1]);
      console.log("Token extracted via regex:", 
        extractedToken.substring(0, 6) + "...");
      return extractedToken;
    }
    
    // Récupérer depuis localStorage en dernier recours
    const storedToken = localStorage.getItem("reset_token");
    const tokenExpiry = localStorage.getItem("reset_token_expiry");
    
    if (storedToken && tokenExpiry) {
      const expiry = parseInt(tokenExpiry);
      if (Date.now() <= expiry) {
        console.log("Using token from localStorage:", 
          storedToken.substring(0, 6) + "...");
        return storedToken;
      } else {
        console.log("Stored token expired");
        localStorage.removeItem("reset_token");
        localStorage.removeItem("reset_token_expiry");
      }
    }
    
    console.error("No token found - Debug info:", {
      fullUrl: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
      locationSearch: location.search,
      locationHash: location.hash,
      hashSplit: location.hash.split('?'),
      searchParamsEntries: Array.from(searchParams.entries())
    });
    
    return null;
  };

  useEffect(() => {
    console.log("PasswordReset component mounted/updated");
    const extractedToken = extractToken();
    
    if (extractedToken) {
      console.log("Token validation starting:", extractedToken.substring(0, 6) + "...");
      setToken(extractedToken);
      setTokenValid(true);
      setErrorMessage(null);
    } else {
      console.error("No valid token found");
      setTokenValid(false);
      setErrorMessage("Le lien de réinitialisation est invalide ou a expiré.");
      toast({
        title: "Lien invalide",
        description: "Le lien de réinitialisation est invalide ou a expiré.",
        variant: "destructive",
      });
    }
  }, [searchParams, location, toast]);

  const handleResetPassword = async (newPassword: string, confirmPassword: string) => {
    if (!tokenValid || !token) {
      toast({
        title: "Erreur",
        description: "Jeton de réinitialisation manquant ou invalide.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 8) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit comporter au moins 8 caractères.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      console.log("Attempting password reset with token:", token.substring(0, 6) + "...");
      const success = await resetPassword(token, newPassword);
      
      if (success) {
        console.log("Password reset successful");
        setShowSuccessDialog(true);
        // Nettoyer le token du localStorage après succès
        localStorage.removeItem("reset_token");
        localStorage.removeItem("reset_token_expiry");
      } else {
        console.error("Password reset failed");
        setErrorMessage("Échec de la réinitialisation. Le lien est invalide ou a expiré.");
        toast({
          title: "Échec de la réinitialisation",
          description: "Le lien de réinitialisation est invalide ou a expiré.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error during password reset:", error);
      setErrorMessage("Une erreur s'est produite lors de la réinitialisation du mot de passe.");
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la réinitialisation du mot de passe.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#00123A] to-[#FCACF4]/40 p-4">
      {!tokenValid ? (
        <InvalidTokenCard
          errorMessage={errorMessage}
          onNavigateToLogin={handleGoToLogin}
        />
      ) : (
        <PasswordResetForm
          onSubmit={handleResetPassword}
          errorMessage={errorMessage}
          isLoading={isLoading}
        />
      )}

      <ResetSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        onNavigateToLogin={handleGoToLogin}
      />
    </div>
  );
}
